import type { NavItem } from '@/config/nav.types';
import type { ProducerCapabilities as RoleCapabilities } from '@/hooks/useProducerAccess';
import type { ProducerMeta, ProducerCapabilityKey } from '@/lib/capabilities';

/**
 * Context needed to evaluate nav item visibility and disabled state.
 * Shared between AppSidebar (desktop) and AppMobileBottomNav (mobile).
 */
export interface NavVisibilityContext {
  enabledCapabilities: string[];
  roleCapabilities?: RoleCapabilities;
  isViewerRole?: boolean;
  producerMeta?: ProducerMeta | null;
  capabilitiesMap?: Record<ProducerCapabilityKey, boolean> | null;
  /** If true, all items shown with on/off badges (admin mode) */
  showAllWithStatusBadges?: boolean;
}

/** Whether the item should appear at all */
export function canShowNavItem(item: NavItem, ctx: NavVisibilityContext): boolean {
  if (ctx.showAllWithStatusBadges) return true;
  // Role capability gate (e.g. canManageTeam)
  if (item.requireRoleCapability) {
    if (ctx.roleCapabilities && ctx.roleCapabilities[item.requireRoleCapability] !== true) {
      return false;
    }
  }
  return true;
}

export interface NavItemDisabledResult {
  disabled: boolean;
  reason?: string;
}

/** Whether the item should be rendered as disabled (visible but not clickable) */
export function isNavItemDisabled(item: NavItem, ctx: NavVisibilityContext): NavItemDisabledResult {
  if (ctx.showAllWithStatusBadges) return { disabled: false };

  const isSuspended = ctx.producerMeta?.isSuspended ?? false;
  if (isSuspended) return { disabled: true, reason: 'Avstängd' };

  if (item.comingSoon) return { disabled: true, reason: 'Kommer snart' };

  if (item.requireCapability && !ctx.enabledCapabilities.includes(item.requireCapability)) {
    return { disabled: true, reason: 'Ej aktiverad' };
  }

  const isVerified = ctx.producerMeta?.isVerified ?? false;
  if (item.requiresVerification && !isVerified) {
    return { disabled: true, reason: 'Kräver verifiering' };
  }

  return { disabled: false };
}
